import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
	ArrowDown,
	ArrowUp,
	ChevronDown,
	ChevronUp,
	ClipboardPaste,
	Copy,
	Eye,
	Loader2,
	Moon,
	MousePointerClick,
	Paintbrush,
	Redo2,
	RotateCcw,
	Save,
	Sun,
	Undo2,
	X,
} from "lucide-react";

import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useWhiteLabel } from "../../context/WhiteLabel";
import { useThemeContext } from "../../context/DarkMode";
import { hslParaHex } from "../../helpers/estiloVisual";
import { cn } from "../../lib/utils";
import CamposDoTema, { Segmentos } from "./CamposDoTema";
import { CHAVES_DE_CAIXA, PROPRIEDADES, TIPOS, paginaDaRota } from ".";

const ATALHOS = [
	{ to: "/", nome: "Dashboard" },
	{ to: "/reports", nome: "Relatórios" },
	{ to: "/monitor", nome: "Monitor" },
	{ to: "/rede", nome: "Rede" },
];

// Opções de forma do item (gravadas em `layout`, valem nos dois modos). Os
// valores passam no formato do backend: número de até 3 dígitos ou palavra.
const OPCOES_DE_CAIXA = {
	borda: {
		nome: "Espessura da borda",
		opcoes: [{ id: "", nome: "Padrão" }, ...[0, 1, 2, 3, 4].map((n) => ({ id: String(n), nome: `${n}px` }))],
	},
	estiloborda: {
		nome: "Estilo da borda",
		opcoes: [
			{ id: "", nome: "Sólida" },
			{ id: "tracejada", nome: "Tracejada" },
			{ id: "nenhuma", nome: "Nenhuma" },
		],
	},
	canto: {
		nome: "Cantos",
		opcoes: [
			{ id: "0", nome: "Retos" },
			{ id: "6", nome: "Pouco" },
			{ id: "", nome: "Padrão" },
			{ id: "20", nome: "Bem arredondados" },
		],
	},
	padding: {
		nome: "Espaçamento interno",
		opcoes: [{ id: "", nome: "Padrão" }, ...[0, 8, 12, 16, 20, 24, 32].map((n) => ({ id: String(n), nome: String(n) }))],
	},
	sombra: {
		nome: "Sombra",
		opcoes: [
			{ id: "", nome: "Padrão" },
			{ id: "nenhuma", nome: "Nenhuma" },
			{ id: "leve", nome: "Leve" },
			{ id: "media", nome: "Média" },
		],
	},
};

// "rgb(1, 2, 3)" -> "#010203", para o <input type="color"> abrir na cor que
// está na tela. O que não for rgb opaco (transparente, oklch) cai no cinza.
const paraHex = (cor) => {
	const m = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/.exec(cor || "");
	if (!m || (m[4] !== undefined && Number(m[4]) === 0)) return "#888888";
	return `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, "0")).join("")}`;
};

const elementoDe = (chave) => document.querySelector(`[data-aparencia="${CSS.escape(chave)}"]`);

const corNaTela = (chave, prop) => {
	const el = elementoDe(chave);
	if (!el) return "#888888";
	const css = getComputedStyle(el);
	if (prop === "borda") return paraHex(css.borderTopColor);
	if (prop === "texto") return paraHex(css.color);
	if (prop === "destaque") return hslParaHex(css.getPropertyValue("--primary").trim());
	return paraHex(css.backgroundColor);
};

// Onde o bloco está: os irmãos (na ordem da tela) e se a largura cabe. Só
// mexe na ordem quando TODO irmão é personalizável — um irmão sem chave
// ficaria com `order: 0` e pularia para o começo. A ordem vem do rascunho,
// não do DOM: no render o DOM ainda é o do clique anterior.
const LARGURA_MINIMA = 1280;

const lerArranjo = (chave, componentes) => {
	const pai = elementoDe(chave)?.parentElement;
	if (!pai) return null;
	const css = getComputedStyle(pai);
	const filhos = [...pai.children];
	const podeMover =
		/flex|grid/.test(css.display) && filhos.length > 1 && filhos.every((f) => f.hasAttribute("data-aparencia"));
	const ordemDe = (c) => Number(componentes[c]?.layout?.ordem ?? 0);
	const irmaos = [...new Set(filhos.map((f) => f.dataset.aparencia))]
		.map((c, i) => ({ c, i }))
		.sort((a, b) => ordemDe(a.c) - ordemDe(b.c) || a.i - b.i)
		.map(({ c }) => c);
	const grade = /grid/.test(css.display);
	const colunas = grade && window.innerWidth >= LARGURA_MINIMA ? css.gridTemplateColumns.split(" ").length : 0;
	return { irmaos, podeMover, grade, colunas };
};

// Os outros itens do mesmo tipo dentro do mesmo pai personalizável (a seção
// do cartão), para "Aplicar a todos".
const vizinhosDoTipo = (chave, tipo) => {
	const secao = elementoDe(chave)?.parentElement?.closest("[data-aparencia]");
	if (!secao) return [];
	const chaves = [...secao.querySelectorAll("[data-aparencia]")]
		.filter((el) => el.dataset.aparenciaTipo === tipo)
		.map((el) => el.dataset.aparencia)
		.filter((c) => c !== chave);
	return [...new Set(chaves)];
};

// Árvore dos itens personalizáveis da página, lida do DOM com o aninhamento
// (seção > cartões) e na ordem da tela (`order` do CSS dentro do mesmo pai).
// Item que não aparece (Bloco vazio com `empty:hidden`) fica fora.
const lerCamadas = (raiz) => {
	const nos = new Map();
	const grupos = new Map();
	const topo = [];
	raiz.querySelectorAll("[data-aparencia]").forEach((el) => {
		if (!el.getClientRects().length) return;
		if (!grupos.has(el.parentElement)) grupos.set(el.parentElement, grupos.size);
		const no = {
			el,
			chave: el.dataset.aparencia,
			nome: el.dataset.aparenciaNome || el.dataset.aparencia.split("|")[1],
			tipo: el.dataset.aparenciaTipo,
			grupo: grupos.get(el.parentElement),
			ordem: Number(getComputedStyle(el).order) || 0,
			filhos: [],
		};
		nos.set(el, no);
		const pai = el.parentElement?.closest("[data-aparencia]");
		(nos.get(pai)?.filhos || topo).push(no);
	});
	const ordenar = (lista) => {
		lista.sort((a, b) => a.grupo - b.grupo || a.ordem - b.ordem);
		lista.forEach((n) => ordenar(n.filhos));
		return lista;
	};
	return ordenar(topo);
};

const realcar = (el, ligado) => el?.toggleAttribute("data-aparencia-realce", ligado);

const semVazios = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v && Object.keys(v).length));

// Só o que é forma (borda, canto...), sem a ordem e a largura do item.
const soCaixa = (layout = {}, manter = true) =>
	Object.fromEntries(Object.entries(layout).filter(([k]) => CHAVES_DE_CAIXA.includes(k) === manter));

// O que conta como "alterado": o que o Salvar grava.
const fotografia = (r) => JSON.stringify([r?.componentes || {}, r?.claro || {}, r?.escuro || {}, r?.paleta || {}]);

// Agrupa as mudanças de um arrasto (a mesma cor, a mesma intensidade) num
// passo só de desfazer. Clique em opção é sempre um passo.
const JANELA_DO_PASSO = 500;

const Titulo = ({ children }) => (
	<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{children}</p>
);

const SecaoDoTema = ({ titulo, descricao, acao, children }) => (
	<section className="space-y-3 border-b pb-4 last:border-b-0">
		<div>
			<Titulo>{titulo}</Titulo>
			{descricao ? <p className="mt-1 text-xs text-muted-foreground">{descricao}</p> : null}
		</div>
		{acao}
		{children}
	</section>
);

const Camadas = ({ lista, nivel = 0, componentes, selecionado, escolher }) =>
	lista.map((no, i) => (
		<React.Fragment key={`${no.chave}-${i}`}>
			<button
				type="button"
				onMouseEnter={() => realcar(no.el, true)}
				onMouseLeave={() => realcar(no.el, false)}
				onFocus={() => realcar(no.el, true)}
				onBlur={() => realcar(no.el, false)}
				onClick={() => escolher(no)}
				style={{ paddingLeft: 8 + nivel * 14 }}
				aria-current={selecionado?.chave === no.chave ? "true" : undefined}
				className={cn(
					"flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
					selecionado?.chave === no.chave && "bg-accent font-medium"
				)}
			>
				<span className="min-w-0 flex-1 truncate">{no.nome}</span>
				<span className="shrink-0 text-[11px] text-muted-foreground">{TIPOS[no.tipo]?.nome}</span>
				<span
					className={cn("h-1.5 w-1.5 shrink-0 rounded-full", componentes[no.chave] ? "bg-primary" : "bg-transparent")}
					aria-label={componentes[no.chave] ? "Personalizado" : undefined}
				/>
			</button>
			{no.filhos.length ? (
				<Camadas
					lista={no.filhos}
					nivel={nivel + 1}
					componentes={componentes}
					selecionado={selecionado}
					escolher={escolher}
				/>
			) : null}
		</React.Fragment>
	));

// Inspetor do super admin para personalizar o estilo clicando na página.
// Aberto pelo editor do estilo (Personalizar nas páginas); some quando sai.
// Do lg para cima é a coluna à direita da página (layout/index.jsx); abaixo,
// folha flutuante.
const Painel = () => {
	const { rascunho, setRascunho, selecionado: escolhido, setSelecionado, previa, setPrevia } = useWhiteLabel();
	const { darkMode, toggleTheme } = useThemeContext();
	const { pathname } = useLocation();
	const navigate = useNavigate();

	const [salvo, setSalvo] = useState(() => fotografia(rascunho));
	const [salvando, setSalvando] = useState(false);
	const [aberto, setAberto] = useState(true);
	const [confirmarSaida, setConfirmarSaida] = useState(false);
	const [camadas, setCamadas] = useState([]);
	const [sobMouse, setSobMouse] = useState("");
	const [copiado, setCopiado] = useState(null);
	const [pilha, setPilha] = useState({ passado: [], futuro: [] });
	const ultimoPasso = useRef({ quando: 0, fonte: null });

	const [aba, setAba] = useState("camadas");
	// A seleção é da página em que foi feita: em outra página não vale.
	const pagina = paginaDaRota(pathname);
	const selecionado = escolhido?.chave.startsWith(`${pagina}|`) ? escolhido : null;

	// Outra página: começa pelas camadas dela.
	const [paginaVista, setPaginaVista] = useState(pagina);
	if (pagina !== paginaVista) {
		setPaginaVista(pagina);
		setAba("camadas");
	}

	// Clicou num item (na página ou nas camadas): o inspetor mostra ele.
	const [chaveVista, setChaveVista] = useState(selecionado?.chave);
	if (selecionado?.chave !== chaveVista) {
		setChaveVista(selecionado?.chave);
		if (selecionado) setAba("item");
	}

	const componentes = rascunho?.componentes || {};
	const alterado = fotografia(rascunho) !== salvo;
	const modo = darkMode ? "escuro" : "claro";

	// Toda mudança passa aqui: guarda o rascunho de antes para o Desfazer.
	// `fonte` identifica o controle arrastado (cor, régua).
	const aplicar = (mudar, fonte) => {
		// Só roda em resposta a clique/arrasto, nunca no render.
		// eslint-disable-next-line react-hooks/purity
		const agora = Date.now();
		const anterior = ultimoPasso.current;
		if (!fonte || fonte !== anterior.fonte || agora - anterior.quando > JANELA_DO_PASSO) {
			setPilha((p) => ({ passado: [...p.passado.slice(-49), rascunho], futuro: [] }));
		}
		ultimoPasso.current = { quando: agora, fonte };
		setRascunho(mudar);
	};

	const desfazer = () => {
		if (!pilha.passado.length) return;
		setPilha({ passado: pilha.passado.slice(0, -1), futuro: [rascunho, ...pilha.futuro] });
		setRascunho(pilha.passado[pilha.passado.length - 1]);
		ultimoPasso.current = { quando: 0, fonte: null };
	};

	const refazer = () => {
		if (!pilha.futuro.length) return;
		setPilha({ passado: [...pilha.passado, rascunho], futuro: pilha.futuro.slice(1) });
		setRascunho(pilha.futuro[0]);
		ultimoPasso.current = { quando: 0, fonte: null };
	};

	// Os atalhos de teclado leem as funções deste render.
	const atalhos = useRef();
	useEffect(() => {
		atalhos.current = { desfazer, refazer, previa };
	});

	useEffect(() => {
		const tecla = (e) => {
			// Na prévia o Esc é da página (fechar menu); sair é pelo botão.
			if (e.key === "Escape") {
				if (!atalhos.current.previa) setSelecionado(null);
				return;
			}
			if (!(e.metaKey || e.ctrlKey)) return;
			const k = e.key.toLowerCase();
			if (k !== "z" && k !== "y") return;
			const alvo = e.target;
			const digitando =
				alvo.isContentEditable ||
				alvo.tagName === "TEXTAREA" ||
				(alvo.tagName === "INPUT" && !["color", "range", "radio", "checkbox", "button"].includes(alvo.type));
			if (digitando) return;
			e.preventDefault();
			if (k === "y" || e.shiftKey) atalhos.current.refazer();
			else atalhos.current.desfazer();
		};
		window.addEventListener("keydown", tecla);
		return () => window.removeEventListener("keydown", tecla);
	}, [setSelecionado]);

	// Fechar a aba com mudança não salva perderia o trabalho.
	useEffect(() => {
		if (!alterado) return undefined;
		const avisar = (e) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", avisar);
		return () => window.removeEventListener("beforeunload", avisar);
	}, [alterado]);

	// Camadas: relidas quando a página muda de conteúdo (dados chegando) ou
	// de ordem. Espera a página assentar para não reler a cada linha.
	useEffect(() => {
		const raiz = document.querySelector("main");
		if (previa || !raiz) return undefined;
		let espera;
		const ler = () => {
			clearTimeout(espera);
			espera = setTimeout(() => setCamadas(lerCamadas(raiz)), 250);
		};
		ler();
		const observador = new MutationObserver(ler);
		observador.observe(raiz, { childList: true, subtree: true });
		return () => {
			clearTimeout(espera);
			observador.disconnect();
			document.querySelectorAll("[data-aparencia-realce]").forEach((el) => realcar(el, false));
		};
	}, [pathname, previa, rascunho?.componentes]);

	// O nome do item sob o ponteiro, no rodapé do inspetor (o item pode não
	// ser `relative`, então a etiqueta não vai na página).
	useEffect(() => {
		if (previa) return undefined;
		const sobre = (e) => {
			const el = e.target.closest?.("main [data-aparencia]");
			setSobMouse(el ? el.dataset.aparenciaNome || "" : "");
		};
		document.addEventListener("mouseover", sobre);
		return () => document.removeEventListener("mouseover", sobre);
	}, [previa]);

	const daPagina = Object.keys(componentes).filter((c) => c.startsWith(`${pagina}|`));
	const itemSelecionado = (selecionado && componentes[selecionado.chave]) || {};
	const cores = itemSelecionado[modo] || {};
	const caixa = soCaixa(itemSelecionado.layout);

	const mudarComponentes = (mudar, fonte) =>
		aplicar((r) => ({ ...r, componentes: semVazios(mudar({ ...(r.componentes || {}) })) }), fonte);

	const mudarCor = (prop, valor) =>
		mudarComponentes((comps) => {
			const item = { ...(comps[selecionado.chave] || {}) };
			const doModo = { ...(item[modo] || {}) };
			if (valor) doModo[prop] = valor;
			else delete doModo[prop];
			item[modo] = doModo;
			comps[selecionado.chave] = semVazios(item);
			return comps;
		}, valor ? `cor:${selecionado.chave}:${modo}:${prop}` : null);

	const mudarLayout = (mudancas) =>
		mudarComponentes((comps) => {
			Object.entries(mudancas).forEach(([chave, parcial]) => {
				const item = { ...(comps[chave] || {}) };
				const layout = { ...(item.layout || {}), ...parcial };
				Object.keys(layout).forEach((k) => layout[k] === "" && delete layout[k]);
				item.layout = layout;
				comps[chave] = semVazios(item);
			});
			return comps;
		});

	// Volta cores (do modo da vez) e forma ao padrão; ordem e largura têm o
	// botão delas.
	const limparItem = () =>
		mudarComponentes((comps) => {
			const item = { ...(comps[selecionado.chave] || {}) };
			delete item[modo];
			item.layout = soCaixa(item.layout, false);
			comps[selecionado.chave] = semVazios(item);
			return comps;
		});

	const estiloDoItem = (chave) => {
		const item = componentes[chave] || {};
		return { claro: item.claro, escuro: item.escuro, layout: soCaixa(item.layout) };
	};

	// Cola cores dos dois modos e forma; a ordem e a largura de cada um ficam.
	const colarEm = (chaves, estilo) =>
		mudarComponentes((comps) => {
			chaves.forEach((c) => {
				const atual = comps[c] || {};
				comps[c] = semVazios({
					claro: estilo.claro,
					escuro: estilo.escuro,
					layout: { ...soCaixa(atual.layout, false), ...estilo.layout },
				});
			});
			return comps;
		});

	const arranjo = selecionado ? lerArranjo(selecionado.chave, componentes) : null;
	const posicao = arranjo ? arranjo.irmaos.indexOf(selecionado.chave) : -1;
	const largura = itemSelecionado.layout?.largura || "";

	// Troca com o vizinho e grava a ordem de todos os irmãos, para a tela e o
	// que foi salvo dizerem a mesma coisa.
	const mover = (passo) => {
		const lista = [...arranjo.irmaos];
		const destino = posicao + passo;
		[lista[posicao], lista[destino]] = [lista[destino], lista[posicao]];
		mudarLayout(Object.fromEntries(lista.map((c, i) => [c, { ordem: String(i) }])));
	};

	const ordemOriginal = () => mudarLayout(Object.fromEntries(arranjo.irmaos.map((c) => [c, { ordem: "" }])));
	const temOrdem = arranjo?.irmaos.some((c) => componentes[c]?.layout?.ordem !== undefined);

	const limparPagina = () =>
		mudarComponentes((comps) => {
			daPagina.forEach((c) => delete comps[c]);
			return comps;
		});

	const escolher = (no) => {
		setSelecionado({ chave: no.chave, nome: no.nome, tipo: no.tipo });
		no.el.scrollIntoView({ block: "center", behavior: "smooth" });
	};

	const salvar = async () => {
		setSalvando(true);
		try {
			await api.put(`/estilos-visuais/${rascunho.id}`, rascunho);
			setSalvo(fotografia(rascunho));
			setConfirmarSaida(false);
			toast.success("Estilo salvo. Quem usa já vê a mudança.");
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const sair = () => {
		if (alterado && !confirmarSaida) {
			setConfirmarSaida(true);
			return;
		}
		const id = rascunho.id;
		setSelecionado(null);
		setPrevia(false);
		setRascunho(null);
		navigate(`/estilos-visuais/${id}`);
	};

	if (previa) {
		return (
			<div className="fixed bottom-20 right-4 z-50 flex items-center gap-3 rounded-full border bg-popover py-1.5 pl-4 pr-1.5 text-popover-foreground shadow-2xl md:bottom-4">
				<Eye className="h-4 w-4 text-primary" aria-hidden="true" />
				<span className="text-sm">Pré-visualizando</span>
				<Button size="sm" className="rounded-full" onClick={() => setPrevia(false)}>
					Voltar a editar
				</Button>
			</div>
		);
	}

	const tipo = selecionado ? TIPOS[selecionado.tipo] || TIPOS.cartao : null;
	const vizinhos = selecionado ? vizinhosDoTipo(selecionado.chave, selecionado.tipo) : [];
	const podeColar = copiado && selecionado && copiado.tipo === selecionado.tipo && copiado.chave !== selecionado.chave;
	const naLista = ATALHOS.some((a) => a.to === pathname);

	return (
		<aside
			aria-label="Personalizar aparência"
			className="fixed bottom-20 right-4 z-50 flex max-h-[70vh] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-2xl md:bottom-4 lg:static lg:max-h-none lg:w-80 lg:max-w-none lg:shrink-0 lg:rounded-none lg:border-0 lg:border-l lg:shadow-none"
		>
			<div className="space-y-2 border-b px-3 py-3">
				<div className="flex items-center gap-2">
					<Paintbrush className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
					<div className="min-w-0 flex-1">
						<p className="truncate text-sm font-semibold">{rascunho.nome}</p>
						<p className="text-xs text-muted-foreground">{alterado ? "Alterações não salvas" : "Tudo salvo"}</p>
					</div>
					<Button size="sm" onClick={salvar} disabled={salvando || !alterado}>
						{salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
						Salvar
					</Button>
					<Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Sair da personalização" title="Sair" onClick={sair}>
						<X className="h-4 w-4" />
					</Button>
					<Button
						size="icon"
						variant="ghost"
						className="h-8 w-8 lg:hidden"
						aria-label={aberto ? "Recolher painel" : "Abrir painel"}
						onClick={() => setAberto((a) => !a)}
					>
						{aberto ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
					</Button>
				</div>
				{confirmarSaida ? (
					<div className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-2.5 py-2">
						<p className="min-w-0 flex-1 text-xs">Sair sem salvar o que mudou?</p>
						<Button size="sm" variant="ghost" className="h-7" onClick={() => setConfirmarSaida(false)}>
							Cancelar
						</Button>
						<Button size="sm" variant="destructive" className="h-7" onClick={sair}>
							Sair sem salvar
						</Button>
					</div>
				) : null}
				<div className="flex items-center gap-1">
					<select
						aria-label="Página"
						value={naLista ? pathname : ""}
						onChange={(e) => navigate(e.target.value)}
						className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						{naLista ? null : <option value="">Esta página</option>}
						{ATALHOS.map((a) => (
							<option key={a.to} value={a.to}>
								{a.nome}
							</option>
						))}
					</select>
					<Button
						size="icon"
						variant="ghost"
						className="h-8 w-8"
						aria-label={darkMode ? "Tema escuro (trocar para o claro)" : "Tema claro (trocar para o escuro)"}
						title={darkMode ? "Tema escuro — as cores valem só para ele" : "Tema claro — as cores valem só para ele"}
						onClick={toggleTheme}
					>
						{darkMode ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
					</Button>
					<Button
						size="icon"
						variant="ghost"
						className="h-8 w-8"
						aria-label="Desfazer"
						title="Desfazer (Ctrl+Z)"
						disabled={!pilha.passado.length}
						onClick={desfazer}
					>
						<Undo2 className="h-4 w-4" />
					</Button>
					<Button
						size="icon"
						variant="ghost"
						className="h-8 w-8"
						aria-label="Refazer"
						title="Refazer (Ctrl+Shift+Z)"
						disabled={!pilha.futuro.length}
						onClick={refazer}
					>
						<Redo2 className="h-4 w-4" />
					</Button>
					<Button
						size="icon"
						variant="ghost"
						className="h-8 w-8"
						aria-label="Pré-visualizar"
						title="Pré-visualizar: a página volta a responder ao clique"
						onClick={() => setPrevia(true)}
					>
						<Eye className="h-4 w-4" />
					</Button>
				</div>
			</div>

			<Tabs value={aba} onValueChange={setAba} className={cn("min-h-0 flex-1 flex-col", aberto ? "flex" : "hidden lg:flex")}>
				<TabsList className="mx-3 mt-3 grid shrink-0 grid-cols-3">
					<TabsTrigger value="camadas">Camadas</TabsTrigger>
					<TabsTrigger value="item">Item</TabsTrigger>
					<TabsTrigger value="tema">Tema</TabsTrigger>
				</TabsList>

				<TabsContent value="camadas" className="min-h-0 flex-1 overflow-auto px-2 pb-3">
					{camadas.length ? (
						<Camadas lista={camadas} componentes={componentes} selecionado={selecionado} escolher={escolher} />
					) : (
						<p className="px-2 py-3 text-sm text-muted-foreground">
							Esta página não tem itens personalizáveis. Escolha outra no seletor acima.
						</p>
					)}
					<div className="mt-3 flex items-center gap-2 border-t px-2 pt-3 text-xs text-muted-foreground">
						<span className="flex-1">
							{daPagina.length === 0
								? "Nada personalizado nesta página."
								: `${daPagina.length} ${daPagina.length === 1 ? "item personalizado" : "itens personalizados"} nesta página.`}
						</span>
						{daPagina.length ? (
							<Button size="sm" variant="ghost" className="h-7" onClick={limparPagina}>
								Limpar página
							</Button>
						) : null}
					</div>
				</TabsContent>

				<TabsContent value="item" className="min-h-0 flex-1 space-y-4 overflow-auto px-3 pb-3">
					{selecionado ? (
						<>
							<div className="flex items-start gap-2">
								<div className="min-w-0 flex-1">
									<p className="truncate text-sm font-medium">{selecionado.nome}</p>
									{tipo.nome !== selecionado.nome ? <p className="text-xs text-muted-foreground">{tipo.nome}</p> : null}
								</div>
								<Button
									size="icon"
									variant="ghost"
									className="h-7 w-7"
									aria-label="Fechar seleção (Esc)"
									onClick={() => setSelecionado(null)}
								>
									<X className="h-4 w-4" />
								</Button>
							</div>

							<div className="space-y-2">
								<Titulo>Cores · {darkMode ? "tema escuro" : "tema claro"}</Titulo>
								{tipo.props.map((prop) => (
									<div key={prop} className="flex items-center gap-3">
										<input
											type="color"
											aria-label={PROPRIEDADES[prop]}
											className="h-8 w-10 shrink-0 cursor-pointer rounded border bg-transparent p-0.5"
											value={cores[prop] || corNaTela(selecionado.chave, prop)}
											onChange={(e) => mudarCor(prop, e.target.value)}
										/>
										<div className="min-w-0 flex-1">
											<p className="truncate text-sm">{PROPRIEDADES[prop]}</p>
											<p className="text-xs text-muted-foreground">{cores[prop] ? cores[prop] : "Padrão do estilo"}</p>
										</div>
										{cores[prop] ? (
											<Button
												size="icon"
												variant="ghost"
												className="h-8 w-8"
												aria-label={`Voltar ${PROPRIEDADES[prop]} ao padrão`}
												onClick={() => mudarCor(prop, "")}
											>
												<RotateCcw className="h-4 w-4" />
											</Button>
										) : null}
									</div>
								))}
							</div>

							<div className="space-y-3 border-t pt-3">
								<Titulo>Forma · nos dois temas</Titulo>
								{tipo.caixa.map((chave) => (
									<div key={chave} className="space-y-1.5">
										<p className="text-sm">{OPCOES_DE_CAIXA[chave].nome}</p>
										<Segmentos
											rotulo={OPCOES_DE_CAIXA[chave].nome}
											opcoes={OPCOES_DE_CAIXA[chave].opcoes}
											valor={caixa[chave] || ""}
											aoMudar={(v) => mudarLayout({ [selecionado.chave]: { [chave]: v } })}
										/>
									</div>
								))}
							</div>

							{arranjo?.podeMover || arranjo?.grade ? (
								<div className="space-y-2 border-t pt-3">
									<Titulo>Posição e largura</Titulo>
									{arranjo.podeMover ? (
										<div className="flex flex-wrap items-center gap-1.5">
											<Button size="sm" variant="outline" disabled={posicao <= 0} onClick={() => mover(-1)}>
												<ArrowUp className="h-4 w-4" />
												Antes
											</Button>
											<Button
												size="sm"
												variant="outline"
												disabled={posicao >= arranjo.irmaos.length - 1}
												onClick={() => mover(1)}
											>
												<ArrowDown className="h-4 w-4" />
												Depois
											</Button>
											<span className="text-xs text-muted-foreground">
												{posicao + 1} de {arranjo.irmaos.length}
											</span>
											{temOrdem ? (
												<Button size="sm" variant="ghost" className="ml-auto h-8" onClick={ordemOriginal}>
													Ordem original
												</Button>
											) : null}
										</div>
									) : null}
									{arranjo.grade ? (
										arranjo.colunas ? (
											<Segmentos
												rotulo="Largura"
												opcoes={[
													{ id: "", nome: "Padrão" },
													...Array.from({ length: arranjo.colunas - 1 }, (_, i) => ({
														id: String(i + 1),
														nome: i ? `${i + 1} colunas` : "1 coluna",
													})),
													{ id: "inteira", nome: "Linha inteira" },
												]}
												valor={largura}
												aoMudar={(v) => mudarLayout({ [selecionado.chave]: { largura: v } })}
											/>
										) : (
											<p className="text-xs text-muted-foreground">
												A largura vale em telas a partir de {LARGURA_MINIMA}px — abra numa tela maior para mudar.
											</p>
										)
									) : null}
								</div>
							) : null}

							<div className="flex flex-wrap gap-1.5 border-t pt-3">
								<Button
									size="sm"
									variant="outline"
									onClick={() => {
										setCopiado({ ...selecionado, estilo: estiloDoItem(selecionado.chave) });
										toast.info(`Estilo de “${selecionado.nome}” copiado.`);
									}}
								>
									<Copy className="h-4 w-4" />
									Copiar estilo
								</Button>
								<Button
									size="sm"
									variant="outline"
									disabled={!podeColar}
									title={
										copiado && !podeColar && copiado.tipo !== selecionado.tipo
											? `O estilo copiado é de ${TIPOS[copiado.tipo]?.nome.toLowerCase()}`
											: undefined
									}
									onClick={() => colarEm([selecionado.chave], copiado.estilo)}
								>
									<ClipboardPaste className="h-4 w-4" />
									Colar estilo
								</Button>
								{vizinhos.length ? (
									<Button
										size="sm"
										variant="outline"
										className="w-full"
										onClick={() => colarEm(vizinhos, estiloDoItem(selecionado.chave))}
									>
										Aplicar a todos os {tipo.plural} desta seção ({vizinhos.length})
									</Button>
								) : null}
								{Object.keys(cores).length || Object.keys(caixa).length ? (
									<Button size="sm" variant="ghost" className="w-full" onClick={limparItem}>
										Voltar este item ao padrão
									</Button>
								) : null}
							</div>
						</>
					) : (
						<div className="flex items-start gap-3 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
							<MousePointerClick className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
							<p>
								Clique numa faixa, seção, painel ou cartão da página — ou escolha em Camadas. Os botões da
								página ficam desligados enquanto você personaliza; use o olho para pré-visualizar.
							</p>
						</div>
					)}
				</TabsContent>

				<TabsContent value="tema" className="min-h-0 flex-1 space-y-4 overflow-auto px-3 pb-3">
					<p className="text-xs text-muted-foreground">
						Vale para todas as páginas. As cores são do {darkMode ? "tema escuro" : "tema claro"} — troque no sol/lua
						acima para mexer no outro.
					</p>
					<CamposDoTema
						estilo={rascunho}
						mudar={(parcial) => aplicar((r) => ({ ...r, ...parcial }), JSON.stringify(Object.keys(parcial)))}
						modo={modo}
						Secao={SecaoDoTema}
					/>
				</TabsContent>
			</Tabs>

			<div className="hidden h-9 shrink-0 items-center gap-2 border-t px-3 text-xs text-muted-foreground lg:flex">
				<MousePointerClick className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
				<span className="truncate">{sobMouse ? `Sob o mouse: ${sobMouse}` : "Passe o mouse num item da página"}</span>
			</div>
		</aside>
	);
};

// Montado só durante a edição: o "salvo" de partida é o do estilo que abriu.
const PainelDeAparencia = () => {
	const { rascunho } = useWhiteLabel();
	return rascunho ? <Painel key={rascunho.id} /> : null;
};

export default PainelDeAparencia;
